/**
 * The box a task's notes are written in — rich text (Aaron, Oct 25, note
 * 1.4: "Definitely need at least bold, highlight, font size, underline,
 * italics, bullet lists, indents, etc.").
 *
 * **TipTap, not `note-editor.tsx`.** The record composer's box is
 * `contentEditable` with `execCommand`: right for four buttons, wrong for
 * this many. Indenting, numbered lists, sizes and links are exactly where
 * `execCommand` stops agreeing with itself across browsers — every browser
 * nests a list its own way, undo forgets what it did, a paste brings a page
 * of markup with it. TipTap (already the terms editor's, MIT, pinned at
 * 3.31.3) keeps the note as a document: undo is real, Tab nests a list item,
 * a paste is cut down to the schema before it lands, and what comes out is
 * the same markup every time. **No new package**: the highlight, the colour
 * and the size are three small marks of ours (`note-rich-schema.ts`) that
 * write the classes `shared/src/rich-text.ts` keeps — TipTap's own Highlight
 * and TextStyle write `<mark>` and inline `style="color:#…;font-size:…px"`,
 * free values the allow-list would have to accept or quietly throw away.
 *
 * **What is stored is still a note**: sanitised HTML in the same small
 * vocabulary, run through `sanitizeNote` on the way out of the editor and
 * again on the server, so the record timeline's `<NoteBody>` renders a task's
 * notes the way the sheet wrote them. A note stored as plain words (made from
 * a text, dictated, or typed before notes were rich) opens a paragraph a line
 * (`noteHtmlOf`) and is only rewritten if somebody edits it.
 *
 * **Loaded on demand**, like `<RichEditor>`: this shell waits for
 * `note-rich-editor-tiptap.tsx`, and nothing else may import that file.
 */
import { Suspense, lazy, type ReactNode } from 'react'
import { noteText } from '@rowted/shared'
import { cn } from '../lib/cn.js'

const Editor = lazy(() => import('./note-rich-editor-tiptap.js'))

/** What a parent may do to the box once it is up — Dictate puts its words in. */
export interface RichNoteHandle {
  /** Adds the words at the end, after a space, as plain text. */
  insertText: (text: string) => void
  focus: () => void
}

export interface RichNoteEditorProps {
  /** The stored note: sanitised HTML, or plain words. */
  value: string
  /** Sanitised HTML, or `''` when nothing is left in it. */
  onChange: (html: string) => void
  /** The box's accessible name — *Notes*. */
  label?: string
  placeholder?: string
  /** The writing area's height before it grows, in px. */
  minHeight?: number
  /** The phone's 15 px words and 36 px buttons, one sideways-scrolling row. */
  phone?: boolean
  /** More controls at the end of the toolbar — *Dictate*. */
  tools?: ReactNode
  onReady?: (handle: RichNoteHandle | null) => void
  onBlur?: () => void
  disabled?: boolean
  /** The overlay layer its menus open on, over the sheet that holds it. */
  layer?: number
  className?: string
  /**
   * The people *@* may name — the record composer's *@ to mention someone*
   * (Aaron, Oct 25: the composer gets the task notes' box). Without them, @
   * is only a character.
   */
  mentions?: readonly { id: string; name: string }[]
}

export function RichNoteEditor(props: RichNoteEditorProps) {
  return (
    <Suspense fallback={<NoteSkeleton {...props} />}>
      <Editor {...props} />
    </Suspense>
  )
}

/**
 * The box while the editor is on its way: the same border, a toolbar-shaped
 * bar and the note's words, so the sheet neither jumps nor goes blank.
 */
function NoteSkeleton({ value, minHeight = 70, phone = false, label = 'Notes', className }: RichNoteEditorProps) {
  return (
    <div className={cn('overflow-hidden rounded-[10px] border border-line bg-surface', className)}>
      <div className={cn('flex items-center gap-1 border-b border-line-faint px-1.5', phone ? 'h-11' : 'h-9')}>
        {[24, 24, 24, 24, 40, 40].map((width, at) => (
          <span key={at} aria-hidden style={{ width }} className="h-5 rounded-md bg-line-soft" />
        ))}
      </div>
      <p aria-label={label} style={{ minHeight }}
        className={cn('m-0 whitespace-pre-wrap px-3 py-2.5 text-text-body', phone ? 'text-[14.5px]' : 'text-[13px]')}>
        {noteText(value)}
      </p>
    </div>
  )
}
